import { SegSelectOption } from "../../../SegUI/SegSelect/SegSelect";
import CodigosDeArea from "../../../../Assets/CodigosDeArea.json";

/**
 * Reglas de validación del cotizador de Auto y Moto.
 *
 * Están copiadas una a una de los slides de una pregunta por pantalla que este
 * flujo reemplaza (InicioNormal, Nombre, Apellido, Email, Telefono,
 * FechaNacimiento, Genero, CodigoPostalManual): mismos regex y mismos mensajes.
 * Al pasar a una sola pantalla ya no las aplica react-hook-form campo por
 * campo, así que viven acá y las usa el formulario unificado.
 */

export const RX_PATENTE = /^[A-Za-z]{3}\d{3}$|^[A-Za-z]{2}\d{3}[A-Za-z]{2}$/;
export const RX_NOMBRE = /^[a-zA-ZáéíóúüñÁÉÍÓÚÜÑ\s]+$/;
export const RX_EMAIL = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/;

export type CampoCotizacion =
  | "marca"
  | "modelo"
  | "anio"
  | "version"
  | "nombre"
  | "apellido"
  | "fechaNac"
  | "genero"
  | "email"
  | "telefono"
  | "provincia"
  | "localidad"
  | "codigoPostal"
  | "cuandoContrata";

/** Lo que el paso de datos tiene cargado en cada momento. */
export interface ValoresCotizacion {
  marca?: SegSelectOption;
  modelo?: SegSelectOption;
  anio?: SegSelectOption;
  version?: SegSelectOption;
  nombre: string;
  apellido: string;
  dia: string;
  mes: string;
  anioNac: string;
  genero?: SegSelectOption;
  email: string;
  prefijo: string;
  numero: string;
  provincia?: SegSelectOption;
  localidad?: SegSelectOption;
  /** Solo en el camino BFF: Segucore no resuelve localidad -> CP. */
  codigoPostal?: string;
  cuandoContrata?: SegSelectOption;
}

export const valoresIniciales: ValoresCotizacion = {
  nombre: "",
  apellido: "",
  dia: "",
  mes: "",
  anioNac: "",
  email: "",
  prefijo: "",
  numero: "",
};

/** Día: 2 dígitos, 1 a 31. Mensajes de FechaNacimiento.tsx. */
export const validarDia = (value: string): string => {
  if (!value) return "Día debe incluir 2 dígitos";
  const parsed = parseInt(value, 10);
  if (isNaN(parsed) || parsed > 31) return "Día no puede ser mayor a 31";
  if (isNaN(parsed) || parsed <= 0) return "Día no puede ser 0";
  if (/^[0-9]$/.test(value)) return "Día debe incluir 2 dígitos";
  return "";
};

/** Mes: 2 dígitos, 1 a 12. Mensajes de FechaNacimiento.tsx. */
export const validarMes = (value: string): string => {
  if (!value) return "Mes debe incluir 2 dígitos";
  const parsed = parseInt(value, 10);
  if (isNaN(parsed) || parsed > 12) return "Mes no puede ser mayor a 12";
  if (isNaN(parsed) || parsed <= 0) return "Mes no puede ser 0";
  if (/^[0-9]$/.test(value)) return "Mes debe incluir 2 dígitos";
  return "";
};

export const validarAnioNacimiento = (value: string): string => {
  if (!value) return "Ingresá el año de nacimiento";
  if (!/^\d{4}$/.test(value)) return "Año debe incluir 4 dígitos";
  return "";
};

/**
 * Edad asegurable: mayores de 20 y menores de 100.
 * Portado tal cual de validateAge() en FechaNacimiento.tsx.
 */
export const validarEdad = (anio: string, mes: string, dia: string): string => {
  const hoy = new Date();
  const anioActual = hoy.getFullYear();
  const mesActual = hoy.getMonth() + 1;
  const diaActual = hoy.getDate();

  const anioIngresado = parseInt(anio, 10);
  const mesIngresado = parseInt(mes, 10);
  const diaIngresado = parseInt(dia, 10);

  if (`${anioIngresado}`.length > 3 && anioActual - anioIngresado > 99) {
    return "Sólo aseguramos a menores de 100 años.";
  }
  if (anioActual - anioIngresado > 20) return "";
  if (anioActual - anioIngresado === 20) {
    if (
      mesActual > mesIngresado ||
      (mesActual === mesIngresado && diaActual >= diaIngresado)
    ) {
      return "";
    }
  }
  return "Sólo aseguramos a mayores de 20 años.";
};

/** Fecha de nacimiento completa: formato de cada parte y después la edad. */
export const validarFechaNacimiento = (
  dia: string,
  mes: string,
  anio: string
): string => {
  return (
    validarDia(dia) ||
    validarMes(mes) ||
    validarAnioNacimiento(anio) ||
    validarEdad(anio, mes, dia)
  );
};

/**
 * Celular. AR: cód. de área de la lista y 10 dígitos entre prefijo y número.
 * UY: no se pide cód. de área y el número son 8 dígitos (Telefono.tsx).
 */
export const validarTelefono = (
  isUY: boolean,
  prefijo: string,
  numero: string
): string => {
  if (isUY) {
    if (!numero) return "Ingresá Número de Celular";
    if (!/^[0-9]{8}$/.test(numero)) return "Número inválido";
    return "";
  }
  if (!prefijo) return "Ingresá el Cód. Área";
  if (!(CodigosDeArea as string[]).includes(prefijo)) return "Cód. Área inválido";
  if (!numero) return "Ingresá Número de Celular";
  if (!/^[0-9]{6,9}$/.test(numero)) return "Número inválido";
  if ((prefijo + numero).length !== 10) return "Número inválido";
  return "";
};

/**
 * Primer año con cobertura disponible. `anios_gracia` (EnvConfig) es una
 * antigüedad máxima en años, no un año: con 30, en 2026 el corte es 1997.
 */
export const anioMaximoCobertura = (aniosGracia: number | string): number =>
  new Date().getFullYear() - parseInt(`${aniosGracia}`, 10) + 1;

const requeridoSelect = (
  valor: SegSelectOption | undefined,
  mensaje: string
): string => (valor?.value ? "" : mensaje);

/** Devuelve "" si el campo está bien, o el mensaje de error a mostrar. */
export const validarCampo = (
  campo: CampoCotizacion,
  valores: ValoresCotizacion,
  opciones: { isUY: boolean; localidadLibre?: boolean }
): string => {
  switch (campo) {
    case "marca":
      return requeridoSelect(valores.marca, "Elegí la marca");
    case "modelo":
      return requeridoSelect(valores.modelo, "Ingresá el modelo");
    case "anio":
      return requeridoSelect(valores.anio, "Elegí el año");
    case "version":
      return requeridoSelect(valores.version, "Ingresá la versión");
    case "nombre":
      if (!valores.nombre) return "Ingresá el nombre";
      return RX_NOMBRE.test(valores.nombre) ? "" : "Nombre inválido";
    case "apellido":
      if (!valores.apellido) return "Ingresá el apellido";
      return RX_NOMBRE.test(valores.apellido) ? "" : "Apellido inválido";
    case "fechaNac":
      return validarFechaNacimiento(valores.dia, valores.mes, valores.anioNac);
    case "genero":
      return requeridoSelect(valores.genero, "Elegí una opción");
    case "email":
      if (!valores.email) return "Ingresá el email";
      return RX_EMAIL.test(valores.email) ? "" : "Email inválido";
    case "telefono":
      return validarTelefono(opciones.isUY, valores.prefijo, valores.numero);
    case "provincia":
      return requeridoSelect(
        valores.provincia,
        opciones.isUY ? "Elegí el departamento" : "Elegí la provincia"
      );
    case "localidad":
      return requeridoSelect(
        valores.localidad,
        opciones.localidadLibre ? "Ingresá la localidad" : "Elegí la localidad"
      );
    case "codigoPostal": {
      const cp = (valores.codigoPostal || "").trim();
      if (!cp) return "Ingresá el código postal";
      return /^\d{4}$/.test(cp) ? "" : "El código postal tiene 4 dígitos";
    }
    case "cuandoContrata":
      return requeridoSelect(valores.cuandoContrata, "Elegí cuándo asegurás");
    default:
      return "";
  }
};

/**
 * Qué campos exige el paso de datos. El bloque del vehículo solo entra en Auto
 * (Moto lo resuelve en el paso 1), y sus selects encadenados solo cuando la
 * patente no trajo los datos.
 */
export const camposRequeridos = (opciones: {
  incluirVehiculo: boolean;
  modoVehiculo: "patente" | "manual";
  /** Camino BFF: el código postal lo carga la persona y es obligatorio. */
  exigirCodigoPostal?: boolean;
}): CampoCotizacion[] => {
  const vehiculo: CampoCotizacion[] = !opciones.incluirVehiculo
    ? []
    : opciones.modoVehiculo === "manual"
    ? ["marca", "modelo", "anio", "version"]
    : ["anio"];

  return [
    ...vehiculo,
    "nombre",
    "apellido",
    "fechaNac",
    "genero",
    "email",
    "telefono",
    "provincia",
    "localidad",
    ...(opciones.exigirCodigoPostal
      ? (["codigoPostal"] as CampoCotizacion[])
      : []),
    "cuandoContrata",
  ];
};
